import type { FC } from 'react';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Url, SCRAPS_FOLDER_ID } from '@wix/da-url';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import type { TabItem } from '@wix/da-ds/pkg/Tabs';
import UnderlinedTabs from '@wix/da-ds/pkg/Tabs/dsPresets/UnderlinedTabs';
import useCanCreateSubfolder from '../../../hooks/useCanCreateSubfolder';
import {
  getActiveSection,
  getStudioParams,
  getIsSubfoldersPage,
  getSelectedFolder,
  getIsRootFolder,
} from '../../../selectors/studio';
import { getHasSubfolders } from '../../../selectors/published';
import { StudioSection } from '../../../types/store';

import s from './PublishedTabs.scss';

interface Props {}

const PublishedTabs: FC<Props> = () => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const { contentType, subfolderId, tierId } = useSelector(getStudioParams);
  const activeSection = useSelector(getActiveSection);
  const selectedFolder = useSelector(getSelectedFolder);
  const canCreateFolder = useCanCreateSubfolder(selectedFolder);
  const hasSubfolders = useSelector(getHasSubfolders);
  const isRootFolder = useSelector(getIsRootFolder);
  const isScrapsFolder = selectedFolder?.folderId === SCRAPS_FOLDER_ID;
  const isMyGalleriesPage = activeSection === StudioSection.PUBLISHED_GALLERIES;
  const isMyTiersPage = activeSection === StudioSection.PUBLISHED_TIERS;
  const showSubGalleriesTab =
    isMobile &&
    !isRootFolder &&
    !isScrapsFolder &&
    !isMyTiersPage &&
    (canCreateFolder || hasSubfolders);
  const isSubfoldersPage = useSelector(getIsSubfoldersPage);
  const hasGalleryAndTier = isMobile && !subfolderId && !isScrapsFolder && !tierId;

  if (!contentType) {
    return null;
  }

  let selectedTab;
  if (isMyGalleriesPage) {
    selectedTab = 'galleries';
  } else if (isMyTiersPage) {
    selectedTab = 'tiers';
  } else if (isSubfoldersPage) {
    selectedTab = 'subfolders';
  } else {
    selectedTab = contentType;
  }

  return (
    <UnderlinedTabs
      className={s['root']}
      uniformTabWidth={isMobile}
      items={
        [
          {
            label: t('studio.header.tabs.published.subtabs.deviations'),
            index: 'deviations',
            href: tierId
              ? Url.studioTier(parseInt(tierId), 'deviations')
              : isScrapsFolder
                ? Url.studioGallery(SCRAPS_FOLDER_ID)
                : Url.studio({
                    section: 'published',
                    folderId: subfolderId,
                    contentType: 'deviations',
                  }),
          },
          !subfolderId &&
            !isScrapsFolder && {
              label: t('studio.header.tabs.published.subtabs.posts'),
              index: 'posts',
              href: tierId
                ? Url.studioTier(parseInt(tierId), 'posts')
                : Url.studio({
                    section: 'published',
                    folderId: subfolderId,
                    contentType: 'posts',
                  }),
            },
          !isScrapsFolder && {
            label: t('studio.header.tabs.published.subtabs.galleries'),
            index: 'galleries',
            href: Url.studio({ published: 'galleries' }),
          },
          hasGalleryAndTier && {
            label: t('studio.header.tabs.published.subtabs.tiers'),
            index: 'tiers',
            href: Url.studio({ published: 'tiers' }),
          },
          showSubGalleriesTab && {
            label: t('studio.header.tabs.published.subtabs.subGalleries'),
            index: 'subfolders',
            href: Url.studio({
              section: 'published',
              folderId: subfolderId,
              contentType: 'subfolders',
            }),
          },
        ].filter(item => !!item) as TabItem[]
      }
      activeIndex={selectedTab}
    />
  );
};

PublishedTabs.displayName = 'PublishedTabs';

export default PublishedTabs;
